import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaSave, FaTimes } from "react-icons/fa";

import {
  DEFAULT_TASK_FORM,
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
} from "../data/tasksKanbanData";
import { buildTaskPayload } from "../utils/tasksKanbanUtils";

export default function TaskQuickCreateCard({
  column,
  associations,
  productsApps,
  saving,
  onClose,
  onCreate,
}) {
  const inputRef = useRef(null);

  const initialForm = useMemo(
    () => ({
      ...DEFAULT_TASK_FORM,
      product_app_id: column?.product_app_id || "",
      statut: column?.statut || column?.status || DEFAULT_TASK_FORM.statut,
    }),
    [column],
  );

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    setFormData(initialForm);
  }, [initialForm]);

  useEffect(() => {
    const root = document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 80);

    const onEsc = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onEsc);

    return () => {
      root.style.overflow = previousOverflow;
      clearTimeout(timer);
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onCreate?.(buildTaskPayload(formData));
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/75 px-4 py-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-white/20"
      >
        <div
          className="h-2"
          style={{ backgroundColor: column?.color || "#38bdf8" }}
        />

        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Nouvelle tâche
            </p>
            <h3 className="text-lg font-bold text-slate-900">
              {column?.title || "Sans application"}
            </h3>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="grid h-10 w-10 place-items-center rounded-full text-slate-500 transition hover:bg-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <Field label="Titre de la carte" required>
            <input
              ref={inputRef}
              type="text"
              name="titre"
              value={formData.titre}
              onChange={handleChange}
              required
              placeholder="Ex : Corriger la page renouvellement..."
              className="input-task-kanban"
            />
          </Field>

          <Field label="Description rapide">
            <textarea
              name="contenu"
              value={formData.contenu}
              onChange={handleChange}
              rows={4}
              placeholder="Notes, contexte, lien, consigne..."
              className="input-task-kanban"
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Application">
              <select
                name="product_app_id"
                value={formData.product_app_id || ""}
                onChange={handleChange}
                className="input-task-kanban"
              >
                <option value="">Sans application</option>

                {[...(productsApps || [])]
                  .sort((a, b) =>
                    String(a.nom_app).localeCompare(String(b.nom_app), "fr"),
                  )
                  .map((app) => (
                    <option key={app.id} value={app.id}>
                      {app.nom_app}
                    </option>
                  ))}
              </select>
            </Field>

            <Field label="Association">
              <select
                name="asso_id"
                value={formData.asso_id}
                onChange={handleChange}
                className="input-task-kanban"
              >
                <option value="">Aucune association</option>

                {[...(associations || [])]
                  .sort((a, b) =>
                    String(a.nom_asso).localeCompare(String(b.nom_asso), "fr"),
                  )
                  .map((association) => (
                    <option key={association.id} value={association.id}>
                      {association.nom_asso}
                    </option>
                  ))}
              </select>
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Catégorie">
              <input
                type="text"
                name="categorie"
                value={formData.categorie}
                onChange={handleChange}
                className="input-task-kanban"
              />
            </Field>

            <Field label="Échéance">
              <input
                type="date"
                name="due_date"
                value={formData.due_date || ""}
                onChange={handleChange}
                className="input-task-kanban"
              />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Statut">
              <select
                name="statut"
                value={formData.statut || "A faire"}
                onChange={handleChange}
                className="input-task-kanban"
              >
                {TASK_STATUS_OPTIONS.map((option) => (
                  <option
                    key={`${option.value}-${option.label}`}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Priorité">
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="input-task-kanban"
              >
                {TASK_PRIORITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-[42px] items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving || !String(formData.titre || "").trim()}
            className="inline-flex min-h-[42px] items-center gap-2 rounded-xl bg-sky-600 px-4 py-2 text-sm font-bold text-white shadow-lg shadow-sky-600/20 transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Création..." : "Créer la carte"}
          </button>
        </div>

        <style>{`
          .input-task-kanban {
            width: 100%;
            min-height: 44px;
            border-radius: 0.75rem;
            border: 1px solid rgb(203 213 225);
            background: white;
            padding: 0.625rem 0.75rem;
            font-size: 0.875rem;
            outline: none;
          }

          .input-task-kanban:focus {
            border-color: rgb(56 189 248);
            box-shadow: 0 0 0 4px rgb(224 242 254);
          }
        `}</style>
      </form>
    </div>,
    document.body,
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}